import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader, CheckCircle2, XCircle } from 'lucide-react';
import './EmailVerification.css';

/**
 * Token-based email verification landing page.
 * Reached when a merchant clicks the link in a /resend-verification email
 * (the legacy mobile resend path). The new web signup flow uses code-based
 * /verify-otp instead — this page exists for the link path only.
 */
const STATE_VERIFYING = 'verifying';
const STATE_SUCCESS = 'success';
const STATE_ERROR = 'error';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [state, setState] = useState(STATE_VERIFYING);
  const [message, setMessage] = useState('');
  const [redirectTo, setRedirectTo] = useState('/onboarding');
  const [canResend, setCanResend] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // StrictMode guard
    ranRef.current = true;

    if (!token) {
      setState(STATE_ERROR);
      setMessage('No verification token provided. Please use the link from your email.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`/api/merchant/auth/verify-email?token=${encodeURIComponent(token)}`);
        const data = await res.json();

        if (res.ok && data.success) {
          setState(STATE_SUCCESS);
          setMessage(data.message || 'Email verified successfully.');
          setRedirectTo(data.redirectTo || '/onboarding');
          return;
        }

        setState(STATE_ERROR);
        setMessage(data.error || 'Verification failed. Please try again.');
        setCanResend(!!data.canResend);
      } catch (err) {
        setState(STATE_ERROR);
        setMessage('Network error. Please check your connection and try again.');
      }
    })();
  }, [token]);

  // Auto-redirect on success after 3s
  useEffect(() => {
    if (state !== STATE_SUCCESS) return;
    const t = setTimeout(() => navigate(redirectTo, { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [state, redirectTo, navigate]);

  return (
    <div className="evx-container">
      <div className="evx-card">
        {state === STATE_VERIFYING && (
          <>
            <div className="evx-icon evx-icon-pending">
              <Loader size={36} className="evx-spin" aria-hidden="true" />
            </div>
            <h1 className="evx-title">Verifying your email</h1>
            <p className="evx-subtitle">Hang tight — this only takes a moment.</p>
          </>
        )}

        {state === STATE_SUCCESS && (
          <>
            <div className="evx-icon evx-icon-success">
              <CheckCircle2 size={36} aria-hidden="true" />
            </div>
            <h1 className="evx-title">Email verified</h1>
            <p className="evx-subtitle">{message}</p>
            <button
              type="button"
              className="evx-cta"
              onClick={() => navigate(redirectTo, { replace: true })}
            >
              Continue
            </button>
            <p className="evx-note">Redirecting automatically…</p>
          </>
        )}

        {state === STATE_ERROR && (
          <>
            <div className="evx-icon evx-icon-error">
              <XCircle size={36} aria-hidden="true" />
            </div>
            <h1 className="evx-title">Verification failed</h1>
            <p className="evx-subtitle">{message}</p>
            <div className="evx-actions">
              {canResend && (
                <Link to="/verify-otp" className="evx-cta">
                  Use a code instead
                </Link>
              )}
              <Link to="/login" className="evx-cta-secondary">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;
